<template>
	<div class="form-section invoice-items-section" @click="closeMenus">
		<div v-if="itemsMissingRates" class="warning-banner">
			Some items are missing rates. Please enter rates to calculate amounts.
		</div>

		<div v-if="showBuildingSelector" class="building-search-row">
			<div class="building-select-wrapper">
				<label for="building-name">Building</label>
				<CatalogSearchInput
					id="building-name"
					:modelValue="selectedBuildingId"
					:options="buildingOptions"
					:disabled="disabled"
					:placeholder="buildingPlaceholder"
					@update:modelValue="$emit('update:selectedBuildingId', $event)"
					class="building-search-input"
				/>
			</div>
		</div>
		<div class="invoice-items-heading">
			<h2 class="invoice-items-title">Invoice Items</h2>
			<div class="range-actions" v-if="canAddRanges">
				<button class="add-range-btn" type="button" @click="$emit('add-date-range')" :disabled="disabled">
					<AppointmentIcon name="plus" />
					<span>Add range</span>
				</button>
				<button class="add-range-btn" type="button" @click="$emit('add-custom-date')" :disabled="disabled">
					<AppointmentIcon name="plus" />
					<span>Add date</span>
				</button>
			</div>
		</div>

		<div v-if="!canAddRanges && buildingScopeMessage" class="no-building-message">
			{{ buildingScopeMessage }}
		</div>
		<div v-else-if="!hasItems" class="no-ranges-message">
			Click "Add range" or "Add date" to include items.
		</div>

		<!-- Date Ranges -->
		<div v-if="canAddRanges" v-for="(entry, idx) in dateRanges" :key="idx" class="invoice-date-section">
			<h3 v-if="showDateSectionTitles" class="invoice-date-title">{{ formatDateRange(entry.dateFrom, entry.dateTo) || 'New date range' }}</h3>
			<div class="date-range-entry" :class="{ 'date-range-entry--empty': !entry.dateFrom || !entry.dateTo }">
				<table class="range-details-table" aria-label="Invoice date range and sources">
					<tbody>
						<tr>
							<th scope="row">Date Range</th>
							<td class="date-range-picker-control range-detail-value">
								<DateRangePicker
									use-fixed
									:dateFrom="entry.dateFrom"
									:dateTo="entry.dateTo"
									trigger-label="Date Range"
									placeholder="Click to Select Dates"
									:disabled="disabled"
									@update="(range) => onDateRangeUpdate(entry, range)"
								>
									<template #trigger-icon><AppointmentIcon name="calendar" :size="14" /></template>
								</DateRangePicker>
							</td>
							<td class="range-detail-actions">
								<button class="range-action-btn remove-range-btn" type="button" @click="$emit('remove-date-range', entry)" :disabled="disabled" title="Remove range">
									<AppointmentIcon name="trash" />
									<span>Remove range</span>
								</button>
							</td>
						</tr>
						<tr v-if="entry.dateFrom && entry.dateTo">
							<th scope="row">Sources</th>
							<td class="range-detail-value">
								<div class="sources-control">
									<CatalogSearchInput
										:modelValue="sourceSelection(entry)"
										:options="sourceOptions"
										:disabled="disabled"
										:multiple="true"
										use-fixed
										placeholder="Select sources"
										class="toolbar-filter-input"
										@update:modelValue="(val) => onSourceSelectionChange(entry, val)"
									/>
									<AppointmentIcon name="chevron-down" :size="14" class="sources-chevron" />
								</div>
							</td>
							<td class="range-detail-actions">
								<div v-if="hasAnySourcesSelected(entry)" class="source-actions">
									<button
										type="button"
										class="range-action-btn clear-sources-btn"
										@click.stop="clearSources(entry)"
										:disabled="disabled"
										aria-label="Reset sources"
										title="Reset sources"
									>
										<AppointmentIcon name="close" />
										<span>Reset sources</span>
									</button>
									<div class="source-items-action">
										<button
											class="range-action-btn toolbar-show-btn"
											type="button"
											@click="$emit('toggle-full-items', entry)"
											:disabled="disabled || entry.loadingFullItems"
											:title="entry.showFullItems && !entry.loadingFullItems ? 'Close source items' : 'Show source items'"
										>
											<AppointmentIcon :name="entry.showFullItems && !entry.loadingFullItems ? 'close' : 'journal'" />
											<span class="source-items-label">
												<span :class="{ 'label-hidden': entry.showFullItems && !entry.loadingFullItems }" :aria-hidden="entry.showFullItems && !entry.loadingFullItems">Show source items</span>
												<span :class="{ 'label-hidden': !entry.showFullItems || entry.loadingFullItems }" :aria-hidden="!entry.showFullItems || entry.loadingFullItems">Close source items</span>
											</span>
										</button>
										<span v-if="entry.loadingFullItems" class="source-items-loading loading-spinner" role="status" aria-label="Loading source items"></span>
									</div>
								</div>
							</td>
						</tr>
						<tr v-if="entry.dateFrom && entry.dateTo && hasPurchasesSource(entry)">
							<th scope="row">Markup</th>
							<td class="range-detail-value">
								<div class="markup-control">
									<div class="amount-mode-toggle" role="group" aria-label="Markup on purchases">
										<button
											type="button"
											class="amount-mode-option"
											:class="{ active: hasPurchaseMarkup(entry) }"
											:aria-pressed="hasPurchaseMarkup(entry)"
											:disabled="disabled"
											@click.stop="setPurchaseMarkup(entry, true)"
										>
											+15%
										</button>
										<button
											type="button"
											class="amount-mode-option"
											:class="{ active: !hasPurchaseMarkup(entry) }"
											:aria-pressed="!hasPurchaseMarkup(entry)"
											:disabled="disabled"
											@click.stop="setPurchaseMarkup(entry, false)"
										>
											None
										</button>
									</div>
								</div>
							</td>
							<td class="range-detail-actions"></td>
						</tr>
					</tbody>
				</table>
				<div v-if="entry.dateFrom && entry.dateTo && hasMissingService(entry)" class="missing-service-message status-message">
					{{ entry.missingServiceCount }} appointment{{ entry.missingServiceCount === 1 ? '' : 's' }} missing a service {{ entry.missingServiceCount === 1 ? 'was' : 'were' }} excluded. Please fix {{ entry.missingServiceCount === 1 ? 'it' : 'them' }} in the Appointments journal.
				</div>

				<div class="items-table-container" v-if="entry.dateFrom && entry.dateTo">
					<table
						class="items-table"
						:class="{
							'full-items-mode': entry.showFullItems,
							'without-markup': entry.showFullItems && !hasPurchaseMarkup(entry),
							'compact-merged': !entry.showFullItems && !useQuantityPricingColumns && !showAppointmentColumns(entry) && !showPurchaseColumns,
							'has-purchase-columns': !entry.showFullItems && showPurchaseColumns,
							'quantity-pricing-layout': !entry.showFullItems && useQuantityPricingColumns,
						}"
					>
						<thead>
							<tr v-if="entry.showFullItems">
								<th>Source</th>
								<th>Description</th>
								<th>{{ fullItemsRefLabel(entry) }}</th>
								<th style="width: 140px; text-align: right;">{{ fullItemsBaseHeader(entry) }}</th>
								<th v-if="hasPurchaseMarkup(entry)" style="width: 140px; text-align: right;">Gross +15%</th>
								<th class="actions-header"></th>
							</tr>
							<tr v-else>
								<th class="description-column">Description</th>
								<th v-if="showAppointmentColumns(entry)" class="flat-ref-column">Flat Ref</th>
								<th v-if="showAppointmentColumns(entry)" class="hours-column">Hours</th>
								<th v-if="useQuantityPricingColumns || showPurchaseColumns" class="quantity-column">Qty</th>
								<th v-if="showRateColumn(entry)" class="rate-column">{{ rateColumnLabel }}</th>
								<th class="amount-column">£ Amount</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(item, rowIdx) in displayedItems(entry)"
								:key="item.__dividerId || item.lineId || rowIdx"
								class="fetched-item-row"
								:class="{ 'building-divider-row': item.__divider }"
							>
								<template v-if="item.__divider">
									<td class="building-divider-cell" :colspan="dividerColSpan(entry)">
										{{ item.label }}
									</td>
								</template>
								<template v-else>
									<!-- Full (unmerged) view -->
									<template v-if="entry.showFullItems">
										<td class="text-value">
											<span>
												{{ fullItemSourceLabel(item) }}
											</span>
										</td>
										<td class="text-value">
											<span class="full-item-title">{{ fullItemPrimaryLabel(item) }}</span>
											<span v-if="fullItemSubtitle(item)" class="full-item-subtitle">
												{{ fullItemSubtitle(item) }}
											</span>
										</td>
										<td class="text-value">{{ fullItemSecondaryLabel(item) }}</td>
										<td class="text-value" style="text-align: right;">
											{{ fullItemBaseLabel(item) }}
										</td>
										<td v-if="hasPurchaseMarkup(entry)" class="text-value" style="text-align: right;">
											{{ fullItemMarkedUpLabel(item) }}
										</td>
										<td class="actions-cell">
											<div class="action-wrapper">
												<button
													type="button"
													class="more-options-btn"
													title="More options"
													:disabled="disabled"
													@click.stop="toggleSourceMenu(entry, item, 'full')"
												>
													<AppointmentIcon name="more" />
												</button>
												<div v-if="isSourceMenuOpen(entry, item, 'full')" class="submenu" @click.stop>
													<button
														type="button"
														class="submenu-btn"
														@click.stop="excludeSourceItem(entry, item)"
														:disabled="disabled"
													>
														Exclude
													</button>
												</div>
											</div>
										</td>
									</template>

									<!-- Merged/editable view -->
									<template v-else>
										<td class="description-cell">
											<template v-if="isPurchaseItem(item)">
												<ServiceDescriptionInput
													:modelValue="item.description"
													@update:modelValue="item.description = $event"
													:options="cosOptions"
													:includeCustomItems="false"
													placeholder="Select COS"
													:disabled="disabled"
												/>
											</template>
											<template v-else>
												<div class="description-stack">
													<ServiceDescriptionInput
														:modelValue="item.description"
														@update:modelValue="item.description = $event"
														:options="serviceOptions"
														placeholder="Enter description"
														:disabled="disabled"
														@service-selected="(service) => onServiceSelected(item, service)"
												v-tooltip
											/>
											<div
												v-if="!useQuantityPricingColumns && canEditAmountMode(item)"
														class="amount-mode-toggle"
														role="group"
														aria-label="Line amount mode"
													>
														<button
															type="button"
															class="amount-mode-option"
															:class="{ active: itemAmountMode(item) === 'calculated' }"
															:disabled="disabled"
															@click.stop="setItemAmountMode(item, 'calculated')"
														>
															Rate
														</button>
														<button
															type="button"
															class="amount-mode-option"
															:class="{ active: itemAmountMode(item) === 'direct' }"
															:disabled="disabled"
															@click.stop="setItemAmountMode(item, 'direct')"
														>
															Amount
														</button>
													</div>
												</div>
											</template>
										</td>
										<td v-if="showAppointmentColumns(entry)" class="flat-ref-column" style="text-align: left;">
											<input
												v-model="item.flatRef"
												type="text"
												:disabled="(!useQuantityPricingColumns && shouldDisableInputs(item)) || disabled"
												class="flat-ref-input"
												:class="{ 'disabled-input': !useQuantityPricingColumns && shouldDisableInputs(item) }"
												style="text-align: right;"
											/>
										</td>
										<td v-if="showAppointmentColumns(entry)" class="hours-column" style="text-align: right;">
											<input
												v-if="shouldDisableInputs(item)"
												:value="formatDisabledZero(item.hours)"
												type="number"
												step="0.1"
												disabled
												class="hours-input disabled-input"
											/>
											<input
												v-else
												v-model="item.hours"
												type="number"
												step="0.1"
												:disabled="disabled"
												class="hours-input"
											/>
										</td>
										<td v-if="useQuantityPricingColumns || showPurchaseColumns" class="quantity-column">
											<input
												:value="useQuantityPricingColumns || isPurchaseItem(item) ? 1 : ''"
												type="number"
												readonly
												class="quantity-input derived-input"
											/>
										</td>
										<td v-if="showRateColumn(entry)" class="rate-column">
											<input
												v-if="useQuantityPricingColumns && canEditUnitPrice(item)"
												:value="item.directAmount ?? ''"
												type="number"
												step="0.01"
												:disabled="disabled"
												@input="onUnitPriceInput(item, $event)"
												@blur="onUnitPriceBlur(item)"
												class="rate-input"
											/>
											<input
												v-else-if="useQuantityPricingColumns"
												:value="formatMoney(displayedAmount(item))"
												type="number"
												step="0.01"
												readonly
												class="rate-input derived-input"
											/>
											<input
												v-else-if="isPurchaseItem(item)"
												:value="formatMoney(displayedAmount(item))"
												type="number"
												step="0.01"
												readonly
												class="rate-input derived-input"
											/>
											<template v-else-if="showAppointmentColumns(entry)">
												<input
													v-if="shouldDisableInputs(item)"
													:value="formatDisabledZero(item.rate)"
													type="number"
													step="0.01"
													disabled
													class="rate-input disabled-input"
												/>
												<input
													v-else
													v-model="item.rate"
													type="number"
													step="0.01"
													:disabled="disabled"
													@blur="onMoneyBlur(item, 'rate')"
													class="rate-input"
													:class="{ 'missing': itemAmountMode(item) === 'calculated' && (item.rate === '' || item.rate === null || item.rate === undefined) }"
												/>
											</template>
										</td>
										<td class="amount-column">
											<input
												v-if="useQuantityPricingColumns"
												:value="formatMoney(displayedAmount(item))"
												type="number"
												step="0.01"
												readonly
												class="amount-input derived-input"
											/>
											<!-- Direct amount is editable if mode is direct -->
											<input
												v-else-if="itemAmountMode(item) === 'direct'"
												v-model="item.directAmount"
												type="number"
												step="0.01"
												:disabled="disabled"
												@blur="onMoneyBlur(item, 'directAmount')"
												class="amount-input direct-mode"
											/>
											<input
												v-else
												:value="formatMoney(item.amount)"
												type="number"
												step="0.01"
												:disabled="disabled"
												class="amount-input"
												readonly
											/>
										</td>
									</template>
								</template>
							</tr>
						</tbody>
					</table>

					<div
						v-if="entry.showFullItems && excludedSourceItems(entry).length"
						class="excluded-source-block"
					>
						<h4 class="excluded-source-title">Excluded source items</h4>
						<table
							class="items-table full-items-mode excluded-items-table"
							:class="{ 'without-markup': !hasPurchaseMarkup(entry) }"
						>
							<thead>
								<tr>
									<th>Source</th>
									<th>Description</th>
									<th>{{ fullItemsRefLabel(entry) }}</th>
									<th style="width: 140px; text-align: right;">{{ fullItemsBaseHeader(entry) }}</th>
									<th v-if="hasPurchaseMarkup(entry)" style="width: 140px; text-align: right;">Gross +15%</th>
									<th class="actions-header"></th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(item, rowIdx) in displayedExcludedSourceItems(entry)"
									:key="item.__dividerId || item.lineId || rowIdx"
									class="fetched-item-row"
									:class="{ 'building-divider-row': item.__divider }"
								>
									<template v-if="item.__divider">
										<td class="building-divider-cell" :colspan="dividerColSpan(entry)">
											{{ item.label }}
										</td>
									</template>
									<template v-else>
										<td class="text-value">
											<span>
												{{ fullItemSourceLabel(item) }}
											</span>
										</td>
										<td class="text-value">
											<span class="full-item-title">{{ fullItemPrimaryLabel(item) }}</span>
											<span v-if="fullItemSubtitle(item, true)" class="full-item-subtitle">
												{{ fullItemSubtitle(item, true) }}
											</span>
										</td>
										<td class="text-value">{{ fullItemSecondaryLabel(item) }}</td>
										<td class="text-value" style="text-align: right;">
											{{ fullItemBaseLabel(item) }}
										</td>
										<td v-if="hasPurchaseMarkup(entry)" class="text-value" style="text-align: right;">
											{{ fullItemMarkedUpLabel(item) }}
										</td>
										<td class="actions-cell">
											<div v-if="canRestoreExcludedItem(item)" class="action-wrapper">
												<button
													type="button"
													class="more-options-btn"
													title="More options"
													:disabled="disabled"
													@click.stop="toggleSourceMenu(entry, item, 'excluded')"
												>
													<AppointmentIcon name="more" />
												</button>
												<div v-if="isSourceMenuOpen(entry, item, 'excluded')" class="submenu" @click.stop>
													<button
														type="button"
														class="submenu-btn"
														@click.stop="restoreSourceItem(entry, item)"
														:disabled="disabled"
													>
														Restore
													</button>
												</div>
											</div>
										</td>
									</template>
								</tr>
							</tbody>
						</table>
						<div class="excluded-source-hint">
							These items are not included in the invoice total.
						</div>
					</div>

					<div class="table-actions-footer" v-if="!entry.showFullItems">
						<div class="custom-item-actions">
							<button
								@click="$emit('add-item-to-range', entry)"
								class="add-custom-item-btn"
								type="button"
								:disabled="disabled"
								title="Add custom item to this date range"
							>
								<AppointmentIcon name="plus" :size="18" />
								Add custom item
							</button>
							<button
								v-if="hasManualItems(entry)"
								@click="$emit('remove-all-custom-items', entry)"
								class="remove-custom-items-btn"
								type="button"
								:disabled="disabled"
								title="Remove all custom items from this date range"
							>
								<BsTrash class="icon-trash" />
								Remove custom items
							</button>
						</div>
					</div>

					<div v-if="shouldShowEmptyMessage(entry)" class="no-items-message status-message">
						{{ emptyItemsMessage(entry) }}
					</div>
				</div>
			</div>
		</div>

		<!-- Custom Dates -->
		<div v-if="canAddRanges" v-for="(customEntry, idx) in customDates" :key="`custom-${idx}`" class="invoice-date-section">
			<h3 v-if="showDateSectionTitles" class="invoice-date-title">{{ formatDateRange(customEntry.date, customEntry.date) || 'New date' }}</h3>
			<div class="date-range-entry" :class="{ 'date-range-entry--empty': !customEntry.date }">
				<table class="range-details-table range-details-table--single" aria-label="Invoice item date">
					<tbody>
						<tr>
							<th scope="row">Date</th>
							<td class="date-range-picker-control range-detail-value">
								<DateRangePicker
									mode="single"
									use-fixed
									:dateFrom="customEntry.date"
									trigger-label="Date"
									placeholder="Click to Select Date"
									:disabled="disabled"
									@update="({ dateFrom }) => customEntry.date = dateFrom"
								>
									<template #trigger-icon><AppointmentIcon name="calendar" :size="14" /></template>
								</DateRangePicker>
							</td>
							<td class="range-detail-actions">
								<button class="range-action-btn remove-range-btn" type="button" @click="$emit('remove-custom-date', customEntry)" :disabled="disabled" title="Remove date">
									<AppointmentIcon name="trash" /><span>Remove date</span>
								</button>
							</td>
						</tr>
					</tbody>
				</table>

				<div v-if="customEntry.date" class="items-table-container">
					<table
						class="items-table"
						:class="{
							'has-purchase-columns': showPurchaseColumns,
							'quantity-pricing-layout': useQuantityPricingColumns,
						}"
					>
						<thead>
							<tr>
								<th class="description-column">Description</th>
								<th class="flat-ref-column">Flat Ref</th>
								<th class="hours-column">Hours</th>
								<th v-if="useQuantityPricingColumns || showPurchaseColumns" class="quantity-column">Qty</th>
								<th class="rate-column">{{ rateColumnLabel }}</th>
								<th class="amount-column">£ Amount</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(item, rowIdx) in customEntry.items" :key="item.lineId || rowIdx" class="fetched-item-row">
								<td class="description-cell">
									<div class="description-stack">
										<ServiceDescriptionInput
											:modelValue="item.description"
											@update:modelValue="item.description = $event"
											:options="serviceOptions"
											placeholder="Enter description"
											:disabled="disabled"
											@service-selected="(service) => onServiceSelected(item, service)"
										v-tooltip
									/>
									<div
										v-if="!useQuantityPricingColumns && canEditAmountMode(item)"
											class="amount-mode-toggle"
											role="group"
											aria-label="Line amount mode"
										>
											<button
												type="button"
												class="amount-mode-option"
												:class="{ active: itemAmountMode(item) === 'calculated' }"
												:disabled="disabled"
												@click.stop="setItemAmountMode(item, 'calculated')"
											>
												Rate
											</button>
											<button
												type="button"
												class="amount-mode-option"
												:class="{ active: itemAmountMode(item) === 'direct' }"
												:disabled="disabled"
												@click.stop="setItemAmountMode(item, 'direct')"
											>
												Amount
											</button>
										</div>
									</div>
								</td>
								<td class="flat-ref-column">
									<input
										v-model="item.flatRef"
										type="text"
										:disabled="(!useQuantityPricingColumns && shouldDisableInputs(item)) || disabled"
										class="flat-ref-input"
										:class="{ 'disabled-input': !useQuantityPricingColumns && shouldDisableInputs(item) }"
									/>
								</td>
								<td class="hours-column">
									<input
										v-if="shouldDisableInputs(item)"
										:value="formatDisabledZero(item.hours)"
										type="number"
										step="0.1"
										disabled
										class="hours-input disabled-input"
									/>
									<input
										v-else
										v-model="item.hours"
										type="number"
										step="0.1"
										:disabled="disabled"
										class="hours-input"
									/>
								</td>
								<td v-if="useQuantityPricingColumns || showPurchaseColumns" class="quantity-column">
									<input
										:value="useQuantityPricingColumns || isPurchaseItem(item) ? 1 : ''"
										type="number"
										readonly
										class="quantity-input derived-input"
									/>
								</td>
								<td class="rate-column">
									<input
										v-if="useQuantityPricingColumns && canEditUnitPrice(item)"
										:value="item.directAmount ?? ''"
										type="number"
										step="0.01"
										:disabled="disabled"
										@input="onUnitPriceInput(item, $event)"
										@blur="onUnitPriceBlur(item)"
										class="rate-input"
									/>
									<input
										v-else-if="useQuantityPricingColumns"
										:value="formatMoney(displayedAmount(item))"
										type="number"
										step="0.01"
										readonly
										class="rate-input derived-input"
									/>
									<input
										v-else-if="isPurchaseItem(item)"
										:value="formatMoney(displayedAmount(item))"
										type="number"
										step="0.01"
										readonly
										class="rate-input derived-input"
									/>
									<template v-else>
										<input
											v-if="shouldDisableInputs(item)"
											:value="formatDisabledZero(item.rate)"
											type="number"
											step="0.01"
											disabled
											class="rate-input disabled-input"
										/>
										<input
											v-else
											v-model="item.rate"
											type="number"
											step="0.01"
											:disabled="disabled"
											@blur="onMoneyBlur(item, 'rate')"
											class="rate-input"
											:class="{ 'missing': itemAmountMode(item) === 'calculated' && (item.rate === '' || item.rate === null || item.rate === undefined) }"
										/>
									</template>
								</td>
								<td class="amount-column">
									<input
										v-if="useQuantityPricingColumns"
										:value="formatMoney(displayedAmount(item))"
										type="number"
										step="0.01"
										readonly
										class="amount-input derived-input"
									/>
									<input
										v-else-if="itemAmountMode(item) === 'direct'"
										v-model="item.directAmount"
										type="number"
										step="0.01"
										:disabled="disabled"
										@blur="onMoneyBlur(item, 'directAmount')"
										class="amount-input direct-mode"
									/>
									<input
										v-else
										:value="formatMoney(item.amount)"
										type="number"
										step="0.01"
										:disabled="disabled"
										class="amount-input"
										readonly
									/>
								</td>
							</tr>
						</tbody>
					</table>

					<div class="table-actions-footer">
						<div class="custom-item-actions">
							<button
								@click="$emit('add-item-to-custom', customEntry)"
								class="add-custom-item-btn"
								type="button"
								:disabled="disabled"
							>
								<AppointmentIcon name="plus" :size="18" />
								Add custom item
							</button>
						</div>
					</div>

					<div class="no-items-message status-message" v-if="!customEntry.items?.length">
						No items
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { format, parseISO } from 'date-fns';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import ServiceDescriptionInput from '@/components/ServiceDescriptionInput.vue';
import { toBig } from '@/composables/useInvoiceCalculations';
import {
	BsTrash,
} from '@kalimahapps/vue-icons';

const props = defineProps({
	showBuildingSelector: {
		type: Boolean,
		default: true,
	},
	dateRanges: {
		type: Array,
		default: () => [],
	},
	customDates: {
		type: Array,
		default: () => [],
	},
	buildingOptions: {
		type: Array,
		default: () => [],
	},
	clientBuildingCount: {
		type: Number,
		default: 0,
	},
	allowWithoutBuilding: {
		type: Boolean,
		default: false,
	},
	serviceOptions: {
		type: Array,
		default: () => [],
	},
	serviceGroupOptions: {
		type: Array,
		default: () => [],
	},
	cosOptions: {
		type: Array,
		default: () => [],
	},
	isFetchingRanges: {
		type: Boolean,
		default: false,
	},
	selectedBuildingId: {
		type: [String, Number],
		default: null,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	useQuantityPricingColumns: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits([
	'update:selectedBuildingId',
	'add-date-range',
	'add-custom-date',
	'remove-date-range',
	'remove-custom-date',
	'add-item-to-range',
	'add-item-to-custom',
	'remove-all-custom-items',
	'fetch-items-for-date-range',
	'toggle-full-items',
	'refresh-range',
]);

const SOURCE_IDS = {
	appointmentsAll: 'appointments:all',
	appointmentsGroupPrefix: 'appointments:group:',
	purchasesAll: 'purchases:all',
	purchasesCosPrefix: 'purchases:cos:',
};

const normalizeIdList = (value) => {
	if (Array.isArray(value)) {
		return value.filter((id) => id !== null && id !== undefined && id !== '');
	}
	if (value === null || value === undefined || value === '') return [];
	return [value];
};

const normalizeRangeSources = (range = {}) => {
	const sources = range?.sources && typeof range.sources === 'object' ? range.sources : null;
	if (sources) {
		const appointments = sources.appointments || {};
		const purchases = sources.purchases || {};
		const appointmentGroups = normalizeIdList(appointments.serviceGroupIds);
		const purchaseCos = normalizeIdList(purchases.cosIds);
		return {
			appointments: {
				enabled: Boolean(appointments.enabled ?? appointments.all ?? appointmentGroups.length),
				all: Boolean(appointments.all),
				serviceGroupIds: appointmentGroups,
			},
			purchases: {
				enabled: Boolean(purchases.enabled ?? purchases.all ?? purchaseCos.length),
				all: Boolean(purchases.all),
				cosIds: purchaseCos,
				markup: purchases.markup !== false,
			},
		};
	}

	const legacyType = range?.sourceType || 'appointments';
	if (legacyType === 'purchases') {
		const cosIds = normalizeIdList(range?.cosId);
		return {
			appointments: { enabled: false, all: false, serviceGroupIds: [] },
			purchases: {
				enabled: true,
				all: !cosIds.length,
				cosIds,
				markup: true,
			},
		};
	}
	if (legacyType === 'custom') {
		return {
			appointments: { enabled: false, all: false, serviceGroupIds: [] },
			purchases: { enabled: false, all: false, cosIds: [], markup: true },
		};
	}
	const serviceGroupIds = normalizeIdList(range?.serviceGroupIds ?? range?.serviceGroupId);
	return {
		appointments: {
			enabled: true,
			all: !serviceGroupIds.length,
			serviceGroupIds,
		},
		purchases: { enabled: false, all: false, cosIds: [], markup: true },
	};
};

const deriveLegacySourceType = (sources = {}, fallback = 'appointments') => {
	const appointmentsEnabled = Boolean(sources?.appointments?.enabled);
	const purchasesEnabled = Boolean(sources?.purchases?.enabled);
	if (appointmentsEnabled && purchasesEnabled) return 'mixed';
	if (purchasesEnabled) return 'purchases';
	if (appointmentsEnabled) return 'appointments';
	return fallback;
};

const hasAppointmentsSource = (entry) => normalizeRangeSources(entry).appointments.enabled;
const hasPurchasesSource = (entry) => normalizeRangeSources(entry).purchases.enabled;
// Purchases are billed at gross +15% unless the range's Markup is set to None.
const hasPurchaseMarkup = (entry) => normalizeRangeSources(entry).purchases.markup;
const hasAnySourcesSelected = (entryOrSources) => {
	if (!entryOrSources) return false;
	const sources = entryOrSources?.appointments ? entryOrSources : normalizeRangeSources(entryOrSources);
	return Boolean(sources?.appointments?.enabled || sources?.purchases?.enabled);
};

const resolveOptionLabel = (option = {}) =>
	option?.name ||
	option?.label ||
	option?.title ||
	option?.attributes?.name ||
	option?.attributes?.label ||
	'Untitled';

const sourceOptions = computed(() => {
	const options = [];
	options.push({ id: SOURCE_IDS.appointmentsAll, label: 'All appointments' });
	(props.serviceGroupOptions || []).forEach((group) => {
		const id = group?.id ?? group?.value;
		if (id === null || id === undefined || id === '') return;
		options.push({
			id: `${SOURCE_IDS.appointmentsGroupPrefix}${id}`,
			label: `Service group: ${resolveOptionLabel(group)}`,
		});
	});
	options.push({ id: SOURCE_IDS.purchasesAll, label: 'All purchases' });
	(props.cosOptions || []).forEach((cos) => {
		const id = cos?.id ?? cos?.value;
		if (id === null || id === undefined || id === '') return;
		options.push({
			id: `${SOURCE_IDS.purchasesCosPrefix}${id}`,
			label: `COS: ${resolveOptionLabel(cos)}`,
		});
	});
	return options;
});

const normalizeSelection = (value) =>
	Array.isArray(value) ? value.filter((id) => id !== null && id !== undefined && id !== '').map(String) : [];

const sourceSelection = (entry) => {
	const sources = normalizeRangeSources(entry);
	const selection = [];
	if (sources.appointments.enabled) {
		if (sources.appointments.all || !sources.appointments.serviceGroupIds.length) {
			selection.push(SOURCE_IDS.appointmentsAll);
		} else {
			sources.appointments.serviceGroupIds.forEach((id) => {
				selection.push(`${SOURCE_IDS.appointmentsGroupPrefix}${id}`);
			});
		}
	}
	if (sources.purchases.enabled) {
		if (sources.purchases.all || !sources.purchases.cosIds.length) {
			selection.push(SOURCE_IDS.purchasesAll);
		} else {
			sources.purchases.cosIds.forEach((id) => {
				selection.push(`${SOURCE_IDS.purchasesCosPrefix}${id}`);
			});
		}
	}
	return selection;
};

const onSourceSelectionChange = (entry, value) => {
	if (!entry) return;
	const prevSelection = normalizeSelection(sourceSelection(entry));
	const nextSelection = normalizeSelection(value);
	const nextSet = new Set(nextSelection);
	const prevSet = new Set(prevSelection);
	const added = nextSelection.filter((id) => !prevSet.has(id));

	const applyExclusiveSelection = (allId, prefix) => {
		const addedAll = added.includes(allId);
		const addedGroup = added.some((id) => id.startsWith(prefix));
		if (addedAll) {
			Array.from(nextSet).forEach((id) => {
				if (id.startsWith(prefix)) {
					nextSet.delete(id);
				}
			});
			return;
		}
		if (addedGroup) {
			nextSet.delete(allId);
		}
	};

	applyExclusiveSelection(SOURCE_IDS.appointmentsAll, SOURCE_IDS.appointmentsGroupPrefix);
	applyExclusiveSelection(SOURCE_IDS.purchasesAll, SOURCE_IDS.purchasesCosPrefix);

	const appointmentGroupIds = Array.from(nextSet)
		.filter((id) => id.startsWith(SOURCE_IDS.appointmentsGroupPrefix))
		.map((id) => id.slice(SOURCE_IDS.appointmentsGroupPrefix.length));
	const purchaseCosIds = Array.from(nextSet)
		.filter((id) => id.startsWith(SOURCE_IDS.purchasesCosPrefix))
		.map((id) => id.slice(SOURCE_IDS.purchasesCosPrefix.length));

	const appointmentsAll = nextSet.has(SOURCE_IDS.appointmentsAll);
	const purchasesAll = nextSet.has(SOURCE_IDS.purchasesAll);
	const { markup } = normalizeRangeSources(entry).purchases;

	const sources = {
		appointments: {
			enabled: appointmentsAll || appointmentGroupIds.length > 0,
			all: appointmentsAll,
			serviceGroupIds: appointmentGroupIds,
		},
		purchases: {
			enabled: purchasesAll || purchaseCosIds.length > 0,
			all: purchasesAll,
			cosIds: purchaseCosIds,
			markup,
		},
	};

	entry.sources = sources;
	entry.sourceType = deriveLegacySourceType(sources, entry.sourceType || 'appointments');
	entry.serviceGroupIds = sources.appointments.serviceGroupIds;
	entry.serviceGroupId = entry.serviceGroupIds[0] ?? null;
	entry.cosId = sources.purchases.cosIds[0] ?? null;
	closeMenus();
};

// The editor reloads the range when its sources change, so the amounts follow at once.
const setPurchaseMarkup = (entry, markup) => {
	if (!entry || hasPurchaseMarkup(entry) === markup) return;
	const sources = normalizeRangeSources(entry);
	entry.sources = { ...sources, purchases: { ...sources.purchases, markup } };
};

const clearSources = (entry) => {
	if (!entry) return;
	entry.sources = {
		appointments: { enabled: false, all: false, serviceGroupIds: [] },
		purchases: { enabled: false, all: false, cosIds: [] },
	};
	entry.sourceType = deriveLegacySourceType(entry.sources, entry.sourceType || 'appointments');
	entry.serviceGroupIds = [];
	entry.serviceGroupId = null;
	entry.cosId = null;
	entry.excludedPurchaseIds = [];
	entry.excludedAppointmentIds = [];
	entry.includedPurchaseIds = [];
	entry.items = Array.isArray(entry.items) ? entry.items.filter((item) => item?.manual) : [];
	entry.fullItems = [];
	entry.missingServiceCount = 0;
	entry.showFullItems = false;
	entry.loadingFullItems = false;
	closeMenus();
	emit('refresh-range', entry);
};

const hasItems = computed(() => {
	return (
		(props.dateRanges && props.dateRanges.length) ||
		(props.customDates && props.customDates.length)
	);
});

const showDateSectionTitles = computed(() => props.dateRanges.length + props.customDates.length > 1);

const buildingNameLookup = computed(() => {
	const lookup = new Map();
	(props.buildingOptions || []).forEach((option) => {
		const id = option?.id ?? option?.value;
		const name = option?.name || option?.label || option?.title || option?.data?.name || '';
		if (id !== null && id !== undefined && name) {
			lookup.set(String(id), name);
		}
	});
	return lookup;
});

const canAddRanges = computed(() => {
	return (
		!!props.selectedBuildingId ||
		Number(props.clientBuildingCount) > 0 ||
		props.allowWithoutBuilding ||
		Boolean(hasItems.value)
	);
});

const buildingPlaceholder = computed(() => {
	const count = Number(props.clientBuildingCount) || 0;
	return count > 0 ? `All buildings (${count})` : 'No linked buildings';
});

const buildingScopeMessage = computed(() => {
	return '';
});

const hasSelectedBuilding = computed(() => {
	return props.selectedBuildingId !== null && props.selectedBuildingId !== undefined && props.selectedBuildingId !== '';
});

const itemsMissingRates = computed(() => {
	const lacksRate = (items = []) =>
		items.some(
			(it) =>
				itemAmountMode(it) === 'calculated' &&
				(it.rate === '' || it.rate === null || it.rate === undefined),
		);
	return (
		props.dateRanges?.some((range) => lacksRate(range.items)) ||
		props.customDates?.some((entry) => lacksRate(entry.items))
	);
});

const hasManualItems = (entry) =>
	Array.isArray(entry?.items) && entry.items.some((item) => item.manual);

const hasFetchedItems = (entry) =>
	Array.isArray(entry?.items) && entry.items.some((item) => !item.manual);

const hasFullItems = (entry) => Array.isArray(entry?.fullItems) && entry.fullItems.length > 0;

const hasMissingService = (entry) =>
	hasAppointmentsSource(entry) && Number(entry?.missingServiceCount) > 0;

const isPurchaseItem = (item) => (item?.sourceType || '') === 'purchases';

const hasAppointmentItems = (entry) =>
	Array.isArray(entry?.items) &&
	entry.items.some((item) => !item?.manual && item?.sourceType === 'appointments');

const showAppointmentColumns = (entry) => hasAppointmentItems(entry);
const hasPurchaseItems = (entry) =>
	Array.isArray(entry?.items) && entry.items.some(isPurchaseItem);
const showPurchaseColumns = computed(() =>
	(props.dateRanges || []).some(hasPurchaseItems) ||
	(props.customDates || []).some(hasPurchaseItems),
);
const showInvoiceAppointmentColumns = computed(() =>
	(props.dateRanges || []).some(hasAppointmentItems) ||
	(props.customDates || []).some(hasAppointmentItems),
);
const rateColumnLabel = computed(() => {
	if (props.useQuantityPricingColumns) return '£ Unit Price';
	if (showPurchaseColumns.value && showInvoiceAppointmentColumns.value) {
		return '£ Rate / Unit Price';
	}
	if (showPurchaseColumns.value) return '£ Unit Price';
	return '£ Rate';
});
const showRateColumn = (entry) =>
	props.useQuantityPricingColumns || showAppointmentColumns(entry) || showPurchaseColumns.value;

const hasAnyItems = (entry) => Array.isArray(entry?.items) && entry.items.length > 0;

const emptyItemsMessage = (entry) => {
	const scopeSuffix = props.selectedBuildingId ? 'this building and date range.' : 'this date range.';
	const sources = normalizeRangeSources(entry);
	if (!hasAnySourcesSelected(sources)) {
		return `Select sources to fetch items for ${scopeSuffix}`;
	}
	if (sources.appointments.enabled && sources.purchases.enabled) {
		return `No appointments or purchases found for ${scopeSuffix}`;
	}
	if (sources.purchases.enabled) {
		return `No purchases found for ${scopeSuffix}`;
	}
	return `No appointments found for ${scopeSuffix}`;
};

const shouldShowEmptyMessage = (entry) => {
	if (!entry) return false;
	const sources = normalizeRangeSources(entry);
	if (entry.showFullItems) {
		return !hasFullItems(entry);
	}
	if (!hasAnySourcesSelected(sources)) {
		return !hasAnyItems(entry);
	}
	return !hasFetchedItems(entry) && !hasMissingService(entry);
};

const resolveBuildingLabel = (item) => {
	if (!item) return '';
	if (item.buildingName) return item.buildingName;
	const buildingId = item.buildingId ?? item.building?.id;
	if (buildingId !== null && buildingId !== undefined) {
		const fromLookup = buildingNameLookup.value.get(String(buildingId));
		if (fromLookup) return fromLookup;
	}
	return item.building?.name || '';
};

const buildDividerRows = (items = [], { includeManual = false } = {}) => {
	if (!Array.isArray(items) || !items.length) return [];
	const groups = new Map();
	const buildingGroups = new Set();

	const ensureGroup = (key, label) => {
		if (!groups.has(key)) {
			groups.set(key, { label, items: [] });
		}
		return groups.get(key);
	};

	items.forEach((item) => {
		if (!item) return;
		const isManual = Boolean(item.manual);
		if (isManual && includeManual) {
			groups.set('__manual__', groups.get('__manual__') || { label: 'Custom items', items: [] });
			groups.get('__manual__').items.push(item);
			return;
		}
		const label = resolveBuildingLabel(item) || 'Unknown building';
		const buildingKey = item.buildingId ?? item.building?.id;
		const key = buildingKey !== null && buildingKey !== undefined ? `building:${buildingKey}` : `building:${label}`;
		buildingGroups.add(key);
		const group = ensureGroup(key, label);
		group.items.push(item);
	});

	const showBuildingDividers = !hasSelectedBuilding.value && buildingGroups.size > 0;
	const rows = [];

	const compareBuildingLabels = (a, b) => {
		const nameA = (a || '').trim();
		const nameB = (b || '').trim();
		if (!nameA && !nameB) return 0;
		if (!nameA) return 1;
		if (!nameB) return -1;
		return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
	};

	const buildingKeys = Array.from(groups.keys()).filter((key) => key !== '__manual__');
	buildingKeys.sort((a, b) => compareBuildingLabels(groups.get(a)?.label, groups.get(b)?.label));
	const orderedKeys = groups.has('__manual__') ? [...buildingKeys, '__manual__'] : buildingKeys;

	orderedKeys.forEach((key, index) => {
		const group = groups.get(key);
		if (!group || !group.items.length) return;
		if (key === '__manual__' || showBuildingDividers) {
			rows.push({
				__divider: true,
				__dividerId: `divider-${key}-${index}`,
				label: group.label,
			});
		}
		group.items.forEach((item) => rows.push(item));
	});
	return rows;
};

const normalizeFullItem = (item) => {
	if (!item || typeof item !== 'object') return item;
	const sourceType = item.sourceType === 'purchases' ? 'purchases' : 'appointments';
	const supplierName = item.supplierName ?? item.supplier?.name ?? item.supplier ?? '';
	const serviceName = item.serviceName ?? item.service?.name ?? item.service ?? item.description ?? '';
	const vendorName = item.vendorName ?? item.vendor?.name ?? item.vendor ?? item.description ?? '';
	const cosName = item.cosName ?? item.cos?.name ?? item.cos ?? '';
	const grossWithMarkup = item.grossWithMarkup ?? item.amount ?? item.directAmount;
	return {
		...item,
		sourceType,
		supplierName,
		serviceName,
		vendorName,
		cosName,
		grossWithMarkup,
		flatRef: item.flatRef ?? '',
	};
};

const fullItemSourceLabel = (item) => (isPurchaseItem(item) ? 'Purchase' : 'Appointment');

const hasFullItemAppointments = (entry) =>
	Array.isArray(entry?.fullItems) &&
	entry.fullItems.some((item) => (item?.sourceType || '') === 'appointments');

const fullItemsRefLabel = (entry) => (hasFullItemAppointments(entry) ? 'Ref / COS' : 'COS');

const fullItemsBaseHeader = (entry) => (hasFullItemAppointments(entry) ? 'Hours / Gross' : 'Gross');

const fullItemPrimaryLabel = (item) => {
	if (isPurchaseItem(item)) {
		return item.vendorName || item.vendor?.name || item.vendor || item.description || '—';
	}
	return item.serviceName || item.service?.name || item.service || item.description || '—';
};

const fullItemSecondaryLabel = (item) => {
	if (isPurchaseItem(item)) {
		if (item.missingCos) return 'Missing COS';
		return item.cosName || item.cos?.name || item.cos || '—';
	}
	return item.flatRef || '—';
};

const fullItemBaseLabel = (item) => {
	if (isPurchaseItem(item)) {
		const value = item.gross ?? item.net;
		const formatted = formatMoney(value);
		return formatted ? `£${formatted}` : '—';
	}
	return item.hours ?? '—';
};

const fullItemMarkedUpLabel = (item) => {
	if (isPurchaseItem(item)) {
		const value = item.grossWithMarkup ?? item.amount ?? item.directAmount;
		const formatted = formatMoney(value);
		return formatted ? `£${formatted}` : '—';
	}
	return '—';
};

const excludedReasonLabel = (item) => {
	if (!item) return '';
	switch (item.excludedReason) {
		case 'refund':
			return 'Refunded';
		case 'missing-service':
			return 'Missing service';
		case 'manual':
			return 'Manually excluded';
		default:
			return '';
	}
};

const fullItemSubtitle = (item, includeReason = false) => {
	const parts = [];
	if (!isPurchaseItem(item)) {
		const supplierName = item.supplierName || item.supplier?.name || item.supplier || '';
		if (supplierName) {
			parts.push(supplierName);
		}
	}
	if (includeReason) {
		const reason = excludedReasonLabel(item);
		if (reason) {
			parts.push(reason);
		}
	}
	const primaryLabel = String(fullItemPrimaryLabel(item)).trim().toLowerCase();
	return [...new Set(parts)].filter((part) => String(part).trim().toLowerCase() !== primaryLabel).join(' · ');
};

const displayedItems = (entry) => {
	if (!entry) return [];
	if (entry.showFullItems && Array.isArray(entry.fullItems)) {
		const items = entry.fullItems.filter((item) => !item?.excluded).map(normalizeFullItem);
		return buildDividerRows(items);
	}
	return buildDividerRows(entry.items || [], { includeManual: true });
};

const excludedSourceItems = (entry) => {
	if (!entry || !Array.isArray(entry.fullItems)) return [];
	return entry.fullItems.filter((item) => item?.excluded).map(normalizeFullItem);
};

const displayedExcludedSourceItems = (entry) => buildDividerRows(excludedSourceItems(entry));

const dividerColSpan = (entry) => {
	if (!entry) return 1;
	if (entry.showFullItems) return hasPurchaseMarkup(entry) ? 6 : 5;
	return 2
		+ (showAppointmentColumns(entry) ? 1 : 0)
		+ (showAppointmentColumns(entry) ? 1 : 0)
		+ (props.useQuantityPricingColumns || showPurchaseColumns.value ? 1 : 0)
		+ (showRateColumn(entry) ? 1 : 0);
};


const formatDateRange = (from, to) => {
	if (!from || !to) return "";
	try {
		const fromDate = parseISO(from);
		const toDate = parseISO(to);
		const formatStr = 'd MMM yyyy';
		if (from === to) return format(fromDate, formatStr);
		return `${format(fromDate, formatStr)} – ${format(toDate, formatStr)}`;
	} catch (e) {
		return `${from} - ${to}`;
	}
};

const onDateRangeUpdate = (entry, range = {}) => {
	if (!entry) return;
	entry.dateFrom = range.dateFrom || '';
	entry.dateTo = range.dateTo || '';
};

const normalizeMoney = (value) => {
	if (value === null || value === undefined || value === '') return '';
	const raw = String(value).trim();
	if (!raw) return '';
	const hasComma = raw.includes(',');
	const hasDot = raw.includes('.');
	const normalized =
		hasComma && !hasDot
			? raw.replace(/[£\s]/g, '').replace(',', '.')
			: raw.replace(/[£\s,]/g, '');
	const cleaned = normalized.trim();
	if (!cleaned) return '';
	if (!/^[+-]?\d*(\.\d+)?$/.test(cleaned)) return value;
	return toBig(cleaned).toFixed(2);
};

const formatMoney = (value) => normalizeMoney(value);

const formatDisabledZero = (value) => {
	if (value === null || value === undefined || value === '') return '';
	const raw = String(value).trim();
	if (!raw) return '';
	const normalized = raw.replace(/[£\s]/g, '').replace(',', '.');
	if (!normalized) return '';
	const numeric = Number(normalized);
	if (Number.isFinite(numeric) && numeric === 0) return '';
	return value;
};

const onMoneyBlur = (item, field) => {
	if (!item || !field) return;
	item[field] = normalizeMoney(item[field]);
};

const itemAmountMode = (item) =>
	(item?.amountMode || '').toLowerCase() === 'direct' ? 'direct' : 'calculated';

const displayedAmount = (item) =>
	itemAmountMode(item) === 'direct' ? item?.directAmount : item?.amount;

const canEditUnitPrice = (item) => Boolean(item?.manual);

const onUnitPriceInput = (item, event) => {
	if (!item || !canEditUnitPrice(item)) return;
	item.amountMode = 'direct';
	item.directAmount = event?.target?.value ?? '';
};

const onUnitPriceBlur = (item) => {
	if (!item || !canEditUnitPrice(item)) return;
	item.amountMode = 'direct';
	onMoneyBlur(item, 'directAmount');
};

const shouldDisableInputs = (item) => itemAmountMode(item) === 'direct';

const canEditAmountMode = (item) => Boolean(item?.manual) && !isPurchaseItem(item);

const toMoneyNumber = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const raw = String(value).trim();
	if (!raw) return null;
	const hasComma = raw.includes(',');
	const hasDot = raw.includes('.');
	const normalized =
		hasComma && !hasDot
			? raw.replace(/[£\s]/g, '').replace(',', '.')
			: raw.replace(/[£\s,]/g, '');
	const cleaned = normalized.trim();
	if (!/^[+-]?(?:\d+|\d*\.\d+)$/.test(cleaned)) return null;
	const numeric = Number(toBig(cleaned));
	return Number.isFinite(numeric) ? numeric : null;
};

const resolveDirectAmount = (item) => {
	const directAmount = toMoneyNumber(item?.directAmount);
	if (directAmount !== null) return directAmount;
	const amount = toMoneyNumber(item?.amount);
	if (amount !== null) return amount;
	const hours = toMoneyNumber(item?.hours);
	const rate = toMoneyNumber(item?.rate);
	if (hours !== null && rate !== null) {
		return Number(toBig(hours).times(rate).toFixed(2));
	}
	return '';
};

const setItemAmountMode = (item, mode) => {
	if (!item) return;
	const nextMode = mode === 'direct' ? 'direct' : 'calculated';
	item.amountMode = nextMode;
	if (nextMode === 'direct') {
		const directAmount = resolveDirectAmount(item);
		item.directAmount = directAmount === '' ? item.directAmount || '' : directAmount;
	}
};

const ensureRangeState = (entry) => {
	if (!entry) return;
	const sources = normalizeRangeSources(entry);
	entry.sources = sources;
	entry.sourceType = deriveLegacySourceType(sources, entry.sourceType || 'appointments');
	entry.serviceGroupIds = sources.appointments.serviceGroupIds;
	entry.serviceGroupId = entry.serviceGroupIds[0] ?? null;
	entry.cosId = sources.purchases.cosIds[0] ?? null;
	entry.excludedPurchaseIds = normalizeIdList(entry.excludedPurchaseIds);
	entry.excludedAppointmentIds = normalizeIdList(entry.excludedAppointmentIds);
	entry.includedPurchaseIds = normalizeIdList(entry.includedPurchaseIds);
	if (!Array.isArray(entry.items)) {
		entry.items = [];
	}
	const legacyType = entry.sourceType;
	const fallbackType = legacyType === 'purchases' || legacyType === 'appointments' ? legacyType : null;
	entry.items.forEach((item) => {
		if (!item || item.manual || item.sourceType) return;
		if (item.cosId || item.vendorId) {
			item.sourceType = 'purchases';
			return;
		}
		if (item.serviceId) {
			item.sourceType = 'appointments';
			return;
		}
		if (fallbackType) {
			item.sourceType = fallbackType;
		}
	});
	if (!Array.isArray(entry.fullItems)) {
		entry.fullItems = [];
	}
};

watch(
	() => [props.dateRanges, props.customDates],
	() => {
		(props.dateRanges || []).forEach(ensureRangeState);
	},
	{ immediate: true },
);

const onServiceSelected = (item, service) => {
	if (!service) return;

	item.description = service.name;
	const isCustom = String(service.id || '').startsWith('custom-');
	const serviceRate = toMoneyNumber(service.rate);

	if (isCustom) {
		item.serviceId = null;
		item.amountMode = 'direct';
		item.manual = true;
		item.flatRef = '';
		item.hours = '';
		item.rate = '';
		item.directAmount = item.directAmount || '';
	} else {
		item.serviceId = service.id;
		if (item.manual && serviceRate === null) {
			item.amountMode = 'direct';
			item.rate = '';
			item.directAmount = item.directAmount ?? '';
			return;
		}
		item.amountMode = 'calculated';
		if (serviceRate !== null) {
			item.rate = serviceRate;
		}
		item.directAmount = null;
	}
};

const openMenu = ref({ entry: null, itemKey: null, scope: '' });

const normalizeSourceId = (value) => {
	if (value === null || value === undefined || value === '') return null;
	return String(value);
};

const getSourceItemKey = (item) => {
	const sourceId = normalizeSourceId(item?.sourceId ?? item?.purchaseId ?? item?.id);
	if (!sourceId) return null;
	const sourceType = isPurchaseItem(item) ? 'purchases' : 'appointments';
	return `${sourceType}:${sourceId}`;
};

const isSourceMenuOpen = (entry, item, scope) => {
	const itemKey = getSourceItemKey(item);
	return (
		openMenu.value.entry === entry &&
		openMenu.value.itemKey === itemKey &&
		openMenu.value.scope === scope
	);
};

const toggleSourceMenu = (entry, item, scope) => {
	if (props.disabled) return;
	const itemKey = getSourceItemKey(item);
	if (!itemKey) return;
	if (isSourceMenuOpen(entry, item, scope)) {
		openMenu.value = { entry: null, itemKey: null, scope: '' };
		return;
	}
	openMenu.value = { entry, itemKey, scope };
};

const closeMenus = () => {
	openMenu.value = { entry: null, itemKey: null, scope: '' };
};

const ensureExcludedPurchaseIds = (entry) => {
	if (!entry) return [];
	if (!Array.isArray(entry.excludedPurchaseIds)) {
		entry.excludedPurchaseIds = [];
	}
	return entry.excludedPurchaseIds;
};

const ensureExcludedAppointmentIds = (entry) => {
	if (!entry) return [];
	if (!Array.isArray(entry.excludedAppointmentIds)) {
		entry.excludedAppointmentIds = [];
	}
	return entry.excludedAppointmentIds;
};

const ensureIncludedPurchaseIds = (entry) => {
	if (!entry) return [];
	if (!Array.isArray(entry.includedPurchaseIds)) {
		entry.includedPurchaseIds = [];
	}
	return entry.includedPurchaseIds;
};

const excludeSourceItem = (entry, item) => {
	const sourceId = normalizeSourceId(item?.sourceId ?? item?.purchaseId ?? item?.id);
	if (!entry || !sourceId) return;
	if (isPurchaseItem(item)) {
		const excluded = ensureExcludedPurchaseIds(entry);
		if (!excluded.some((id) => normalizeSourceId(id) === sourceId)) {
			excluded.push(sourceId);
		}
		const included = ensureIncludedPurchaseIds(entry);
		const includedIndex = included.findIndex((id) => normalizeSourceId(id) === sourceId);
		if (includedIndex !== -1) {
			included.splice(includedIndex, 1);
		}
	} else {
		const excluded = ensureExcludedAppointmentIds(entry);
		if (!excluded.some((id) => normalizeSourceId(id) === sourceId)) {
			excluded.push(sourceId);
		}
	}
	closeMenus();
	emit('refresh-range', entry);
};

const restoreSourceItem = (entry, item) => {
	const sourceId = normalizeSourceId(item?.sourceId ?? item?.purchaseId ?? item?.id);
	if (!entry || !sourceId) return;
	if (isPurchaseItem(item)) {
		if (item?.excludedReason === 'refund') {
			const included = ensureIncludedPurchaseIds(entry);
			if (!included.some((id) => normalizeSourceId(id) === sourceId)) {
				included.push(sourceId);
			}
		}
		const excluded = ensureExcludedPurchaseIds(entry);
		const excludedIndex = excluded.findIndex((id) => normalizeSourceId(id) === sourceId);
		if (excludedIndex !== -1) {
			excluded.splice(excludedIndex, 1);
		}
	} else {
		const excluded = ensureExcludedAppointmentIds(entry);
		const excludedIndex = excluded.findIndex((id) => normalizeSourceId(id) === sourceId);
		if (excludedIndex !== -1) {
			excluded.splice(excludedIndex, 1);
		}
	}
	closeMenus();
	emit('refresh-range', entry);
};

const canRestoreExcludedItem = (item) => {
	if (!item?.excluded) return false;
	if (item.excludedReason === 'missing-service') return false;
	return true;
};
</script>

<style scoped>
/* Variables emulation for scoped usage */
.form-section {
	margin-top: 0;
}

.invoice-items-section {
	--toolbar-control-height: 40px;
}

.warning-banner {
	background: var(--noc-orange-10, #fff4e5);
	border: 1px solid var(--noc-orange-30, #f5b971);
	color: var(--noc-orange-90, #8a4b0f);
	padding: 10px 12px;
	border-radius: 4px;
	font-size: 0.9rem;
	margin-bottom: 12px;
}

.building-search-row {
	display: flex;
	flex-direction: row;
	gap: 24px;
	margin-bottom: 24px;
	align-items: flex-end;
	flex-wrap: wrap;
}

.invoice-items-heading {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px 16px;
	margin-bottom: 16px;
}

.invoice-items-title {
	margin: 0;
	color: var(--text-color);
	font: 600 1.25rem/1.75rem var(--font-family-display, "SF Pro Display", sans-serif);
}

.range-actions {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
}

.building-select-wrapper {
	display: flex;
	flex-direction: column;
	gap: 4px; /* spacing-xsmall */
}

.building-all-helper {
	display: block;
	margin-top: 4px;
	font-size: 0.8rem;
	color: var(--text-color-secondary);
}

.building-select-wrapper label {
	display: block;
	margin-bottom: 4px; /* spacing-xsmall */
	font-size: 0.9rem;
	color: var(--text-color);
	font-weight: 500;
}

.building-search-input {
	width: 250px;
}

/* Deep selector for CatalogSearchInput if needed, matching InvoiceItemsManager.vue */
.building-search-input:deep(.field-input) {
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	padding: 6px 12px;
	line-height: 1;
	transition: border-color 0.1s ease;
	font-size: 0.85rem;
	font-family: inherit;
	height: 38px;
	box-sizing: border-box;
	box-shadow: var(--box-shadow);
}

.building-search-input:deep(.field-input:hover) {
	border-color: var(--border-color-active);
}

.building-search-input:deep(.field-input:focus) {
	border-color: var(--text-color, #333);
}

.building-search-input:deep(.field-input::placeholder) {
	color: var(--text-color-secondary, #9ca3af);
	opacity: 0.6;
}

.add-range-btn,
.add-custom-item-btn,
.range-action-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 0 13px;
	height: 34px;
	background: var(--noc-background, #fff);
	border: 1px solid var(--border-color);
	border-radius: 8px;
	color: var(--text-color);
	font: 500 0.875rem/1.25rem var(--font-family-base);
	white-space: nowrap;
	cursor: pointer;
	touch-action: manipulation;
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
	box-shadow: none;
	box-sizing: border-box;
}

.add-range-btn:hover:not(:disabled),
.add-custom-item-btn:hover:not(:disabled),
.range-action-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.add-range-btn:focus-visible,
.add-custom-item-btn:focus-visible,
.range-action-btn:focus-visible {
	outline: none;
	background: var(--noc-background-secondary, #fafafa);
}

.add-range-btn:disabled,
.add-custom-item-btn:disabled,
.range-action-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.no-building-message, .no-ranges-message {
	font-size: 0.9rem;
	color: var(--text-color-secondary);
	padding: 0;
	background-color: transparent;
	border-radius: 0;
	text-align: left;
	margin-top: 12px;
}

/* Date Range Entry */
.invoice-date-section {
	margin: 24px 0;
}

.invoice-date-title {
	margin: 0 0 10px;
	color: var(--text-color);
	font: 600 1rem/1.5rem var(--font-family-display, "SF Pro Display", sans-serif);
}

.date-range-entry {
	border: 0;
	padding: 0;
	margin: 0;
	border-radius: 0;
	background: transparent;
	box-shadow: none;
}

.range-details-table tr:first-child > :not(.range-detail-actions) {
	border-top: 1px solid var(--border-color);
}

.range-details-table {
	width: 100%;
	max-width: 1128px;
	table-layout: fixed;
	border-spacing: 0;
}

@media (min-width: 1001px) {
	.range-details-table--single {
		/* 360px for the label and date, matching half of the invoice header. */
		max-width: 768px;
	}
}

.range-details-table .remove-range-btn {
	color: var(--noc-red, #d32f2f);
}

.range-details-table th {
	width: 132px;
	box-sizing: border-box;
	padding: 8px 12px;
	border-left: 1px solid var(--border-color);
	border-right: 1px solid var(--border-color);
	border-bottom: 1px solid var(--border-color);
	background: var(--noc-background-secondary, #fafafa);
	text-align: left;
	font: 500 13px/18px var(--font-family-base, sans-serif);
	color: var(--text-color-secondary);
}

.range-detail-value {
	padding: 0;
	border-right: 1px solid var(--border-color);
	border-bottom: 1px solid var(--border-color);
	background: var(--noc-background, #fff);
}

.range-detail-actions {
	width: 408px;
	box-sizing: border-box;
	padding: 0 12px;
	vertical-align: middle;
}

.source-actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

.source-items-action {
	position: relative;
	display: inline-flex;
	flex-shrink: 0;
	padding-right: 22px;
}

.source-items-label {
	display: grid;
}

.source-items-label > span {
	grid-area: 1 / 1;
}

.source-items-label .label-hidden {
	visibility: hidden;
}

.source-items-loading {
	position: absolute;
	right: 0;
	top: calc(50% - 7px);
}

.sources-control {
	position: relative;
	min-width: 0;
}

.markup-control {
	display: flex;
	align-items: center;
	min-height: 40px;
	padding: 0 12px;
	box-sizing: border-box;
}

.markup-control .amount-mode-toggle {
	margin-right: 0;
}

/* Sized like the other text in the range table, not the compact toggle in item rows. */
.markup-control .amount-mode-option {
	min-width: 56px;
	padding: 0 10px;
	font-size: 13px;
}

.sources-chevron {
	position: absolute;
	right: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: var(--text-color-secondary);
}

@media (max-width: 1000px) {
	.range-detail-actions { width: 240px; padding: 0 8px; }
	.range-detail-actions .range-action-btn:not(.clear-sources-btn) { width: 40px; padding: 0; }
	.range-detail-actions .range-action-btn:not(.clear-sources-btn) > span:not(.appointment-icon, .loading-spinner) {
		position: absolute;
		width: 1px;
		height: 1px;
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip-path: inset(50%);
		white-space: nowrap;
	}
}

.date-range-picker-control {
	padding: 0;
	background: var(--noc-background, #fff);
	cursor: pointer;
}

.date-range-picker-control :deep(.date-range-picker) {
	display: flex;
	align-items: stretch;
	width: 100%;
	min-width: 0;
}

.date-range-picker-control :deep(.drp-trigger) {
	display: flex;
	flex-direction: row-reverse;
	justify-content: space-between;
	gap: 8px;
	width: 100%;
	min-width: 0;
	height: auto;
	min-height: 40px;
	padding: 10px 12px;
	border: 0;
	border-radius: 0;
	background: transparent;
	color: var(--text-color);
	font: 400 14px/20px var(--font-family-base, sans-serif);
	box-sizing: border-box;
	box-shadow: none;
	outline: none;
	cursor: pointer;
	touch-action: manipulation;
	transition: background-color 0.1s ease;
}

.date-range-picker-control :deep(.drp-trigger:hover:not(:disabled)),
.date-range-picker-control :deep(.drp-trigger[aria-expanded='true']),
.date-range-picker-control :deep(.drp-trigger:focus-visible) {
	background: var(--noc-background-secondary, #fafafa);
	outline: none;
}

.date-range-picker-control :deep(.drp-trigger-text) {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	color: inherit;
	font: inherit;
}

.toolbar-source {
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 220px;
}

.source-toggle {
	display: inline-flex;
	border-radius: 8px;
}

.source-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0 12px;
	height: var(--toolbar-control-height);
	min-height: var(--toolbar-control-height);
	border: 1px solid var(--border-color, #dcdcdc);
	background: var(--noc-background, #fff);
	color: var(--text-color);
	cursor: pointer;
	font-size: 0.85rem;
	font-weight: 500;
	transition: border-color 0.1s ease, background-color 0.1s ease, color 0.1s ease;
	margin-left: -1px;
	position: relative;
}

.source-btn:first-child {
	margin-left: 0;
	border-top-left-radius: 8px;
	border-bottom-left-radius: 8px;
}

.source-btn:last-child {
	border-top-right-radius: 8px;
	border-bottom-right-radius: 8px;
}

.source-btn:hover:not(:disabled) {
	background-color: var(--noc-background-secondary);
	z-index: 1;
}

.source-btn.active {
	background-color: var(--noc-blue-05, #eff6ff);
	border-color: var(--noc-blue, #0065ff);
	color: var(--noc-blue, #0065ff);
	z-index: 2;
}

.source-btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
	background-color: var(--noc-background-secondary);
	z-index: 0;
}

.toolbar-filter-input {
	flex: 1 1 auto;
	min-width: 0;
	height: auto;
	min-height: 0;
}

.toolbar-filter-input.catalog-search-input :deep(.field-input.multi-input) {
	border: 0;
	background: transparent;
	font: 400 14px/20px var(--font-family-base, sans-serif);
	width: 100%;
	height: auto;
	min-height: var(--toolbar-control-height);
	padding: 6px 36px 6px 12px;
	box-sizing: border-box;
	border-radius: 0;
	transition: background-color 0.1s ease;
	box-shadow: none;
	outline: none;
	align-items: center;
	gap: 6px;
}

.toolbar-filter-input.catalog-search-input :deep(.multi-chip) {
	min-width: 0;
	max-width: 100%;
	box-sizing: border-box;
	white-space: normal;
}

.toolbar-filter-input.catalog-search-input :deep(.chip-label) {
	min-width: 0;
	overflow-wrap: anywhere;
}

.toolbar-filter-input.catalog-search-input :deep(.chip-remove) {
	flex: none;
}

.toolbar-filter-input.catalog-search-input :deep(.multi-input-field) {
	padding: 0 !important;
	margin: 0;
	font: inherit;
	background: transparent;
	height: auto !important;
	min-height: 0 !important;
}

.toolbar-filter-input.catalog-search-input:not(.disabled) :deep(.multi-input:hover),
.toolbar-filter-input.catalog-search-input :deep(.multi-input:focus-within) {
	background: var(--noc-background-secondary, #fafafa);
}

.source-items-loading.loading-spinner {
	display: inline-block;
	width: 14px;
	height: 14px;
	box-sizing: border-box;
	border: 2px solid var(--border-color);
	border-top-color: var(--noc-blue);
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

/* Items Table */
.items-table-container {
	margin-top: 16px;
}

.range-details-table + .items-table-container {
	margin-top: 24px;
}

.missing-service-message + .items-table-container {
	margin-top: 8px;
}

.items-table {
	width: 100%;
	box-sizing: border-box;
	border-collapse: separate;
	border-spacing: 0;
	border: none;
	border-inline: 1px solid var(--border-color);
	border-radius: 0;
	background-color: var(--noc-background);
	font: 400 0.875rem/1.25rem var(--font-family-base);
	table-layout: fixed;
}

.actions-header,
.actions-cell {
	width: 42px;
	text-align: center;
}

.action-wrapper {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
}

.more-options-btn {
	background-color: transparent;
	border: none;
	cursor: pointer;
	color: var(--text-color-secondary, #9ca3af);
	font-size: 1.1rem;
	padding: 0;
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 40px;
	line-height: 1;
	touch-action: manipulation;
}

.more-options-btn:hover:not(:disabled),
.more-options-btn:focus-visible {
	background-color: var(--noc-background-secondary, #fafafa);
	outline: none;
}

.more-options-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.submenu {
	position: absolute;
	right: 0;
	top: 100%;
	background-color: var(--noc-background, #fff);
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 4px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	z-index: 10;
	min-width: 140px;
	padding: 4px 0;
}

.submenu-btn {
	display: block;
	width: 100%;
	padding: 8px 16px;
	background: none;
	border: none;
	color: var(--text-color, #000);
	text-align: left;
	font-size: 14px;
	cursor: pointer;
	white-space: nowrap;
	font-weight: 500;
}

.submenu-btn:hover {
	background-color: var(--noc-background-secondary, #f5f5f5);
}

.submenu-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.submenu-btn:disabled:hover {
	background-color: transparent;
}

.items-table thead {
	background-color: #fafafa;
}

.items-table th {
	padding: 10px 16px;
	text-align: left;
	border-top: 1px solid var(--border-color);
	border-bottom: 1px solid var(--border-color);
	vertical-align: middle;
	font: 600 0.8125rem/1.125rem var(--font-family-base);
	color: var(--text-color);
	text-transform: none;
}

.items-table tbody tr {
	transition: background-color 0.1s ease;
}

.items-table td {
	padding: 0;
	text-align: left;
	vertical-align: middle;
	font: inherit;
	color: var(--text-color);
	border: none;
	border-bottom: 1px solid var(--border-color);
}

.items-table .building-divider-row td {
	padding: 8px 16px;
	background-color: var(--noc-blue-05);
	color: var(--noc-blue);
	font-size: 0.75rem;
	font-weight: 700;
	text-transform: uppercase;
	text-align: left;
}

.items-table .building-divider-cell {
	border-right: none;
}

.items-table td:not(:last-child) {
	border-right: 1px solid var(--border-color);
}

.items-table .text-value {
	display: table-cell;
	padding: 10px 16px;
	color: var(--text-color);
	font: inherit;
	vertical-align: middle;
}

.full-item-title {
	font-weight: 400;
}

.full-item-subtitle {
	color: var(--text-color-secondary);
}

.full-item-subtitle::before {
	content: ' · ';
}

/* Column Widths */
.items-table th:nth-child(1), .items-table td:nth-child(1) { width: 45%; } /* Description */
.items-table th:nth-child(2), .items-table td:nth-child(2) { width: 13.75%; text-align: right; } /* Flat Ref */
.items-table th:nth-child(3), .items-table td:nth-child(3) { width: 13.75%; text-align: right; } /* Hours */
.items-table th:nth-child(4), .items-table td:nth-child(4) { width: 13.75%; text-align: right; } /* Rate */
.items-table th:nth-child(5), .items-table td:nth-child(5) { width: 13.75%; text-align: right; } /* Amount */

.items-table:not(.full-items-mode) .description-column { width: auto; }
.items-table:not(.full-items-mode) .flat-ref-column { width: 120px; }
.items-table:not(.full-items-mode) .hours-column { width: 100px; text-align: right; }
.items-table:not(.full-items-mode) .quantity-column { width: 70px; text-align: right; }
.items-table:not(.full-items-mode) .rate-column { width: 110px; text-align: right; }
.items-table:not(.full-items-mode) .amount-column { width: 120px; text-align: right; }

.items-table.compact-merged th:nth-child(1),
.items-table.compact-merged td:nth-child(1) {
	width: 70%;
	text-align: left;
}

.items-table.compact-merged th:nth-child(2),
.items-table.compact-merged td:nth-child(2) {
	width: 30%;
	text-align: right;
}

/* Full Items Mode (Unmerged) overrides */
.items-table.full-items-mode th:nth-child(1),
.items-table.full-items-mode td:nth-child(1) {
	width: 12%;
	text-align: left;
}
.items-table.full-items-mode th:nth-child(2),
.items-table.full-items-mode td:nth-child(2) {
	width: 32%;
	text-align: left;
}
.items-table.full-items-mode th:nth-child(3),
.items-table.full-items-mode td:nth-child(3) {
	width: 18%;
	text-align: left;
}
.items-table.full-items-mode th:nth-child(4),
.items-table.full-items-mode td:nth-child(4) {
	width: 14%;
	text-align: right;
}
.items-table.full-items-mode th:nth-child(5),
.items-table.full-items-mode td:nth-child(5) {
	width: 14%;
	text-align: right;
}
.items-table.full-items-mode th:nth-child(6),
.items-table.full-items-mode td:nth-child(6) {
	text-align: center;
}
/* Without Gross +15% the actions come fifth, and Description takes the freed width. */
.items-table.full-items-mode.without-markup th:nth-child(2),
.items-table.full-items-mode.without-markup td:nth-child(2) {
	width: 46%;
}
.items-table.full-items-mode.without-markup th:nth-child(5),
.items-table.full-items-mode.without-markup td:nth-child(5) {
	width: 42px;
	text-align: center;
}

/* Include description inputs and keep the editor's generic sizing out of cells. */
.invoice-items-section .items-table :deep(input[type]) {
	width: 100%;
	height: 40px;
	min-height: 40px;
	box-sizing: border-box;
	padding: 10px 16px;
	border: none;
	border-radius: 0;
	background-color: transparent;
	color: var(--text-color);
	font: 400 0.875rem/1.25rem var(--font-family-base);
	outline: none;
	transition: background-color 0.1s ease;
}

.hours-input, .quantity-input, .rate-input, .amount-input {
	text-align: right;
}

.items-table input.derived-input {
	cursor: default;
}

/* Highlight on hover/focus */
.invoice-items-section .items-table :deep(input:hover:not(:disabled):not([readonly])),
.invoice-items-section .items-table :deep(input:focus) {
	background-color: var(--noc-background-secondary);
}

.description-stack {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
}

.description-stack :deep(.service-description-input) {
	flex: 1 1 auto;
	min-width: 0;
}

.description-cell :deep(.service-description-input input::placeholder) {
	color: var(--text-color-secondary);
	opacity: 0.6;
}

.amount-mode-toggle {
	display: inline-flex;
	flex: 0 0 auto;
	margin-right: 12px;
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 5px;
	overflow: hidden;
	background: var(--noc-background, #fff);
}

.amount-mode-option {
	height: 28px;
	min-width: 44px;
	padding: 0 7px;
	border: 0;
	border-right: 1px solid var(--border-color, #dcdcdc);
	background: transparent;
	color: var(--text-color-secondary, #666);
	font-size: 0.68rem;
	font-weight: 600;
	font-family: inherit;
	cursor: pointer;
	line-height: 1;
}

.amount-mode-option:last-child {
	border-right: 0;
}

.amount-mode-option.active {
	background: var(--noc-blue-05, #eff6ff);
	color: var(--noc-blue, #0065ff);
}

.amount-mode-option:hover:not(:disabled) {
	background: var(--noc-background-secondary, #f5f5f5);
}

.amount-mode-option.active:hover:not(:disabled) {
	background: var(--noc-blue-05, #eff6ff);
}

.amount-mode-option:disabled {
	opacity: 0.55;
	cursor: not-allowed;
}

.items-table input.missing {
	box-shadow: inset 0 0 0 2px var(--noc-orange-50, #f39c12);
}

.items-table input.disabled-input {
	background-color: transparent;
	color: var(--text-color);
}

.amount-input.direct-mode {
	background-color: transparent;
	color: var(--text-color);
}

.amount-input.direct-mode:hover, 
.amount-input.direct-mode:focus {
	background-color: var(--noc-background-secondary);
}

.items-table input[type='number']::-webkit-outer-spin-button,
.items-table input[type='number']::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.items-table input[type='number'] {
	-moz-appearance: textfield;
	appearance: textfield;
}

.table-actions-footer {
	padding: 12px;
}

.custom-item-actions {
	display: flex;
	gap: 8px;
	align-items: center;
}

.remove-custom-items-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	padding: 0 12px;
	height: 38px;
	min-height: 38px;
	background: var(--noc-background, #fff);
	border: 1px solid var(--border-color);
	border-radius: 8px;
	color: var(--text-color, #333);
	cursor: pointer;
	font-size: 0.85rem;
	font-weight: 500;
	font-family: inherit;
	transition: border-color 0.1s ease;
	box-shadow: var(--box-shadow);
}

.remove-custom-items-btn:hover {
	background: var(--noc-background, #fff);
	border-color: var(--border-color-active);
}

.missing-service-message {
	margin: 8px 16px 0;
	padding: 0;
	font: 400 13px/20px var(--font-family-base);
	color: var(--noc-orange-90, #8a4b0f);
}

.no-items-message {
	font-size: 0.9rem;
	padding: 8px 16px;
	border-radius: 4px;
	margin-top: 8px;
	text-align: center;
	color: var(--text-color-secondary);
	background-color: var(--noc-background-secondary);
}

.excluded-source-block {
	margin-top: 16px;
}

.excluded-source-title {
	margin: 0;
	padding: 0 16px 10px;
	font: 600 14px/20px var(--font-family-base);
	color: var(--text-color);
}

.excluded-source-hint {
	margin: 0;
	padding: 10px 16px 12px;
	font: 400 13px/18px var(--font-family-base);
	color: var(--text-color-secondary);
}
</style>
